import { get, put } from './db';
import { STORE } from './db';

const KEY = 'library-deleted';

export interface LibraryTombstone {
  sha1: string;
  deletedAt: number;
}

interface Row {
  key: string;
  value: LibraryTombstone[];
}

export async function listTombstones(): Promise<LibraryTombstone[]> {
  const row = await get<Row>(STORE.settings, KEY);
  return row?.value ?? [];
}

export async function saveTombstones(entries: LibraryTombstone[]): Promise<void> {
  await put(STORE.settings, { key: KEY, value: entries } satisfies Row);
}

/** 同じ sha1 は新しい deletedAt を残す。 */
export function mergeTombstones(
  a: LibraryTombstone[],
  b: LibraryTombstone[],
): LibraryTombstone[] {
  const map = new Map<string, number>();
  for (const e of [...a, ...b]) {
    const prev = map.get(e.sha1) ?? 0;
    if (e.deletedAt > prev) map.set(e.sha1, e.deletedAt);
  }
  return [...map.entries()]
    .map(([sha1, deletedAt]) => ({ sha1, deletedAt }))
    .sort((x, y) => x.sha1.localeCompare(y.sha1));
}

/**
 * 手元に「削除より後に追加された」ROM がある場合は墓標を外す（再投入）。
 */
export function pruneRevivedTombstones(
  deleted: LibraryTombstone[],
  living: Array<{ sha1: string; addedAt: number }>,
): LibraryTombstone[] {
  const addedAt = new Map(living.map((r) => [r.sha1, r.addedAt]));
  return deleted.filter((d) => {
    const at = addedAt.get(d.sha1);
    return at === undefined || at < d.deletedAt;
  });
}

export async function recordTombstone(sha1: string, deletedAt = Date.now()): Promise<void> {
  const current = await listTombstones();
  await saveTombstones(mergeTombstones(current, [{ sha1, deletedAt }]));
}

export async function clearTombstone(sha1: string): Promise<void> {
  const next = (await listTombstones()).filter((e) => e.sha1 !== sha1);
  await saveTombstones(next);
}

export async function isTombstoned(sha1: string): Promise<boolean> {
  return (await listTombstones()).some((e) => e.sha1 === sha1);
}
